/* eslint-disable sonarjs/no-duplicate-string */
/* eslint-disable sonarjs/cognitive-complexity */

import React, { useState, useEffect, useRef } from 'react';
import {
  Grid,
  RACTable,
  RACTableCell,
  RACTableRow,
  Typography,
  CircularProgress,
  Tooltip,
} from '@rentacenter/racstrap';
import { useTranslation } from 'react-i18next';

interface HistorySearchResult {
  storeNumber: string;
  facturaDateTime: string;
  transactionDateTime: string;
  globalUuid: string;
  creditMemoUuid: string;
  status: string;
  storeTotal: string;
  invoiceTotal: string;
  pendingReceiptUuid: string;
}

interface GlobalFacturaHistorySearchResultProps {
  results: HistorySearchResult[];
  onLoadMore?: () => void;
  hasMoreData?: boolean;
  loadingMore?: boolean;
}

type SortField =
  | 'storeNumber'
  | 'facturaDateTime'
  | 'transactionDateTime'
  | 'status'
  | 'storeTotal'
  | 'invoiceTotal';
type SortDirection = 'asc' | 'desc';

export const GlobalFacturaHistorySearchResult: React.FC<GlobalFacturaHistorySearchResultProps> =
  ({ results, onLoadMore, hasMoreData = true, loadingMore = false }) => {
    const { t } = useTranslation('translation');
    const [sortField, setSortField] = useState<SortField | null>(null);
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const tableContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const handleScroll = () => {
        if (
          !tableContainerRef.current ||
          !onLoadMore ||
          !hasMoreData ||
          loadingMore
        )
          return;

        const { scrollTop, scrollHeight, clientHeight } =
          tableContainerRef.current;
        if (scrollTop + clientHeight >= scrollHeight - 5) {
          onLoadMore();
        }
      };

      const container = tableContainerRef.current;
      if (container) {
        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
      }
    }, [onLoadMore, hasMoreData, loadingMore]);

    const handleSort = (field: SortField) => {
      if (sortField === field) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSortField(field);
        setSortDirection('asc');
      }
    };

    const sortedResults = [...results].sort((a, b) => {
      if (!sortField) return 0;

      let aValue: string | number;
      let bValue: string | number;

      switch (sortField) {
        case 'storeTotal':
        case 'invoiceTotal':
          aValue = parseFloat(a[sortField].replace(/[$,\s]/g, ''));
          bValue = parseFloat(b[sortField].replace(/[$,\s]/g, ''));
          break;
        default:
          aValue = a[sortField];
          bValue = b[sortField];
      }

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      return sortDirection === 'asc'
        ? (aValue as number) - (bValue as number)
        : (bValue as number) - (aValue as number);
    });

    const renderUuidWithTooltip = (uuid: string, maxLength = 8) => {
      if (!uuid || uuid === '-' || uuid.length <= maxLength) {
        return <Typography>{uuid}</Typography>;
      }
      return (
        <Tooltip title={uuid} placement="top">
          <Typography
            style={{
              cursor: 'pointer',
            }}
          >
            {uuid.substring(0, maxLength)}...
          </Typography>
        </Tooltip>
      );
    };

    const searchResultsTableHead = () => (
      <>
        <RACTableCell
          style={{
            fontWeight: 600,
            backgroundColor: '#fff',
            padding: '12px',
            cursor: 'pointer',
            border: 'none',
          }}
          onClick={() => handleSort('storeNumber')}
        >
          {t('Store#')}{' '}
          <span style={{ color: '#0d6efd', fontWeight: 'bold' }}>
            {sortField === 'storeNumber'
              ? sortDirection === 'asc'
                ? '↓'
                : '↑'
              : '↑'}
          </span>
        </RACTableCell>
        <RACTableCell
          style={{
            fontWeight: 600,
            backgroundColor: '#fff',
            padding: '12px',
            cursor: 'pointer',
            border: 'none',
          }}
          onClick={() => handleSort('facturaDateTime')}
        >
          {t('Factura Date & Time')}{' '}
          <span style={{ color: '#0d6efd', fontWeight: 'bold' }}>
            {sortField === 'facturaDateTime'
              ? sortDirection === 'asc'
                ? '↓'
                : '↑'
              : '↑'}
          </span>
        </RACTableCell>
        <RACTableCell
          style={{
            fontWeight: 600,
            backgroundColor: '#fff',
            padding: '12px',
            cursor: 'pointer',
            border: 'none',
          }}
          onClick={() => handleSort('transactionDateTime')}
        >
          {t('Transaction Date & Time')}{' '}
          <span style={{ color: '#0d6efd', fontWeight: 'bold' }}>
            {sortField === 'transactionDateTime'
              ? sortDirection === 'asc'
                ? '↓'
                : '↑'
              : '↑'}
          </span>
        </RACTableCell>
        <RACTableCell
          style={{
            fontWeight: 600,
            backgroundColor: '#fff',
            padding: '12px',
            border: 'none',
          }}
        >
          {t('Global UUID')}
        </RACTableCell>
        <RACTableCell
          style={{
            fontWeight: 600,
            backgroundColor: '#fff',
            padding: '12px',
            border: 'none',
          }}
        >
          {t('Credit Memo UUID')}
        </RACTableCell>
        <RACTableCell
          style={{
            fontWeight: 600,
            backgroundColor: '#fff',
            padding: '12px',
            cursor: 'pointer',
            border: 'none',
          }}
          onClick={() => handleSort('status')}
        >
          {t('Status')}{' '}
          <span style={{ color: '#0d6efd', fontWeight: 'bold' }}>
            {sortField === 'status'
              ? sortDirection === 'asc'
                ? '↓'
                : '↑'
              : '↑'}
          </span>
        </RACTableCell>
        <RACTableCell
          style={{
            fontWeight: 600,
            backgroundColor: '#fff',
            padding: '12px',
            cursor: 'pointer',
            border: 'none',
          }}
          onClick={() => handleSort('storeTotal')}
        >
          {t('Store Total Transaction')}{' '}
          <span style={{ color: '#0d6efd', fontWeight: 'bold' }}>
            {sortField === 'storeTotal'
              ? sortDirection === 'asc'
                ? '↓'
                : '↑'
              : '↑'}
          </span>
        </RACTableCell>
        <RACTableCell
          style={{
            fontWeight: 600,
            backgroundColor: '#fff',
            padding: '12px',
            cursor: 'pointer',
            border: 'none',
          }}
          onClick={() => handleSort('invoiceTotal')}
        >
          {t('Invoice Total Transaction')}{' '}
          <span style={{ color: '#0d6efd', fontWeight: 'bold' }}>
            {sortField === 'invoiceTotal'
              ? sortDirection === 'asc'
                ? '↓'
                : '↑'
              : '↑'}
          </span>
        </RACTableCell>
        <RACTableCell
          style={{
            fontWeight: 600,
            backgroundColor: '#fff',
            padding: '12px',
            border: 'none',
          }}
        >
          {t('Pending Receipt UUID')}
        </RACTableCell>
      </>
    );

    const searchResultsTableBody = () => (
      <>
        {sortedResults.map((result, index) => (
          <RACTableRow
            key={`${result.storeNumber}-${index}`}
            backgroundColor="#fff"
          >
            <RACTableCell
              style={{
                padding: '12px',
                color: '#4A5174',
                fontWeight: 600,
                fontFamily: 'Open Sans',
              }}
            >
              <Typography>{result.storeNumber}</Typography>
            </RACTableCell>
            <RACTableCell
              style={{
                padding: '12px',
                color: '#4A5174',
                fontWeight: 600,
                fontFamily: 'Open Sans',
              }}
            >
              <Typography>{result.facturaDateTime}</Typography>
            </RACTableCell>
            <RACTableCell
              style={{
                padding: '12px',
                color: '#4A5174',
                fontWeight: 600,
                fontFamily: 'Open Sans',
              }}
            >
              <Typography>{result.transactionDateTime}</Typography>
            </RACTableCell>
            <RACTableCell
              style={{
                padding: '12px',
                color: '#4A5174',
                fontWeight: 600,
                fontFamily: 'Open Sans',
              }}
            >
              {renderUuidWithTooltip(result.globalUuid)}
            </RACTableCell>
            <RACTableCell
              style={{
                padding: '12px',
                color: '#4A5174',
                fontWeight: 600,
                fontFamily: 'Open Sans',
              }}
            >
              {renderUuidWithTooltip(result.creditMemoUuid)}
            </RACTableCell>
            <RACTableCell
              style={{
                padding: '12px',
                color: '#4A5174',
                fontWeight: 600,
                fontFamily: 'Open Sans',
              }}
            >
              <Typography>{t(result.status)}</Typography>
            </RACTableCell>
            <RACTableCell
              style={{
                padding: '12px',
                color: '#4A5174',
                fontWeight: 600,
                fontFamily: 'Open Sans',
              }}
            >
              <Typography>{result.storeTotal}</Typography>
            </RACTableCell>
            <RACTableCell
              style={{
                padding: '12px',
                color: '#4A5174',
                fontWeight: 600,
                fontFamily: 'Open Sans',
              }}
            >
              <Typography>{result.invoiceTotal}</Typography>
            </RACTableCell>
            <RACTableCell
              style={{
                padding: '12px',
                color: '#4A5174',
                fontWeight: 600,
                fontFamily: 'Open Sans',
              }}
            >
              <Typography>{result.pendingReceiptUuid}</Typography>
            </RACTableCell>
          </RACTableRow>
        ))}
      </>
    );

    return (
      <Grid container style={{ padding: '16px', backgroundColor: '#f5f5f5' }}>
        <Grid item xs={12}>
          <Typography
            variant="h6"
            style={{ marginBottom: '16px', fontWeight: 600 }}
          >
            {t('Search Results')}
          </Typography>
        </Grid>

        <Grid item xs={12}>
          <div
            ref={tableContainerRef}
            style={{
              maxHeight: '400px',
              overflowY: 'auto',
              borderRadius: '15px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            }}
          >
            <RACTable
              style={{
                backgroundColor: '#fff',
                marginBottom: '16px',
                overflow: 'hidden',
              }}
              renderTableHead={searchResultsTableHead}
              renderTableContent={() => (
                <>
                  {searchResultsTableBody()}
                  {loadingMore && (
                    <RACTableRow backgroundColor="#fff">
                      <RACTableCell
                        colSpan={9}
                        style={{
                          textAlign: 'center',
                          padding: '20px',
                        }}
                      >
                        <CircularProgress
                          size={24}
                          style={{
                            color: '#4285f4',
                          }}
                        />
                      </RACTableCell>
                    </RACTableRow>
                  )}
                </>
              )}
            />
          </div>
        </Grid>
      </Grid>
    );
  };
